import React, { useEffect, useCallback } from 'react';
import Button from './Button';
import usePageScrollLock from '../utils/usePageScrollLock';
import { AdminModal, Btn, RingLine } from './admin/shell/ui';
import { ModalContext } from './modalContext';

export {
  ModalContext, useInModal, MODAL_FIELD, MODAL_WELL, MODAL_ERROR, MODAL_LABEL, MODAL_NOTE, modalChoice, modalRow,
} from './modalContext';

/**
 * The app's modal, on the Portal vocabulary (Oct 26 2026): a white radius-28 card with the
 * ring hairline along its top, a round grey ✕, pill buttons, no borders anywhere. The
 * bordered black-and-white card it replaced survives nowhere — every Button inside a Modal
 * becomes a pill through ModalContext, and the fields / wells / error band the modals draw
 * come from `modalContext.js` so they all read the same.
 */
export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
  showCloseButton = true,
  className = '',
}) {
  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-4xl',
  };

  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  usePageScrollLock(isOpen);
  useEffect(() => {
    if (!isOpen) return undefined;
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      onClose();
    }
  };

  const hasHeader = Boolean(title || showCloseButton);

  return (
    <ModalContext.Provider value={true}>
      <div
        // z-[120]: above every full-screen surface in the app (composer 95, story viewer 100).
        // A confirm that opens behind the thing it is asking about reads as a dead button.
        className="fixed inset-0 safe-inset z-[120] flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.55)' }}
        onClick={handleOverlayClick}
        data-testid="modal-overlay"
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title || undefined}
          className={`relative flex max-h-[90vh] w-full ${sizes[size]} flex-col overflow-hidden rounded-[28px] bg-white font-mono text-black ${className}`}
        >
          <RingLine />

          {hasHeader && (
            <header className="flex items-center justify-between gap-3 px-6 pb-3 pt-6">
              {title ? (
                <h2 className="m-0 min-w-0 font-mono text-[14px] font-bold uppercase leading-[1.3] tracking-[0.14em] text-black">
                  {title}
                </h2>
              ) : <span />}
              {showCloseButton && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#F2F2F2] text-black transition-colors duration-200 hover:bg-black hover:text-white"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </header>
          )}

          <div className={`min-h-0 flex-1 overflow-y-auto px-6 ${hasHeader ? 'pb-6 pt-1' : 'py-6'}`}>
            {children}
          </div>

          {footer && (
            <footer className="flex flex-wrap justify-end gap-2 px-6 pb-6">
              {footer}
            </footer>
          )}
        </div>
      </div>
    </ModalContext.Provider>
  );
}

/**
 * Confirmation modal for delete/destructive actions
 */
export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'CONFIRM ACTION',
  message,
  confirmLabel = 'CONFIRM',
  cancelLabel = 'CANCEL',
  variant = 'danger',
  isLoading = false,
  theme = 'light',
  secondLabel,
  onSecond,
}) {
  if (theme === 'admin') {
    return (
      <AdminConfirm
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={onConfirm}
        onSecond={onSecond}
        title={title}
        message={message}
        confirmLabel={confirmLabel}
        cancelLabel={cancelLabel}
        secondLabel={secondLabel}
        variant={variant}
        isLoading={isLoading}
      />
    );
  }
  if (theme === 'portal') {
    return (
      <PortalConfirm
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={onConfirm}
        title={title}
        message={message}
        confirmLabel={confirmLabel}
        cancelLabel={cancelLabel}
        isLoading={isLoading}
      />
    );
  }
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelLabel}
          </Button>
          {secondLabel && (
            <Button variant="secondary" onClick={onSecond} disabled={isLoading}>
              {secondLabel}
            </Button>
          )}
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'PROCESSING...' : confirmLabel}
          </Button>
        </>
      }
    >
      <p className="m-0 font-mono text-[13px] leading-[1.65] text-[#4B5563]">
        {message}
      </p>
    </Modal>
  );
}

/**
 * The confirm as the staff panel draws it: the panel's white radius-28 modal with the
 * ring-gradient hairline and pill buttons. Same promise as every other confirm, so a
 * caller only changes `theme` — and it takes a SECOND action, which is what the
 * handoff's "ONLY NEW HIRES / APPLY TO ALL n" needs (that resolves `'second'`).
 */
function AdminConfirm({ isOpen, onClose, onConfirm, onSecond, title, message, confirmLabel, cancelLabel, secondLabel, variant, isLoading }) {
  return (
    <AdminModal
      open={isOpen}
      onClose={onClose}
      title={title}
      width={secondLabel ? 560 : 480}
      footer={(
        <>
          <Btn size="sheet" onClick={onClose} disabled={isLoading}>{cancelLabel}</Btn>
          {secondLabel && <Btn size="sheet" onClick={onSecond} disabled={isLoading}>{secondLabel}</Btn>}
          <Btn
            size="sheet"
            variant={variant === 'danger' ? 'red' : 'solid'}
            onClick={onConfirm}
            disabled={isLoading}
            data-testid="admin-confirm-go"
          >
            {isLoading ? 'WORKING…' : confirmLabel}
          </Btn>
        </>
      )}
    >
      <p className="m-0 font-mono text-[13px] leading-[1.65] text-[#4B5563]">{message}</p>
    </AdminModal>
  );
}

/**
 * The confirm as the Portal page draws it: a black panel on the viewer's dark ground with a
 * hard red shadow, cancel on the left, the red thing on the right. Same promise, same keys,
 * different room — the white card would read as the app interrupting the Portal.
 */
function PortalConfirm({ isOpen, onClose, onConfirm, title, message, confirmLabel, cancelLabel, isLoading }) {
  const handleEscape = useCallback((e) => { if (e.key === 'Escape') onClose(); }, [onClose]);
  usePageScrollLock(isOpen);
  useEffect(() => {
    if (!isOpen) return undefined;
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleEscape]);
  if (!isOpen) return null;

  const btn = {
    fontFamily: 'inherit', fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', padding: 13,
    cursor: 'pointer', transition: 'background-color .2s, color .2s', textTransform: 'uppercase',
  };
  return (
    <div
      className="fixed inset-0 safe-inset z-[120] flex items-center justify-center p-5"
      style={{ background: 'rgba(0,0,0,0.75)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="portal-confirm"
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full font-mono text-white"
        style={{ maxWidth: 420, background: '#000', border: '2px solid #fff', boxShadow: '4px 4px 0 0 #FF2D2D', padding: 22 }}
      >
        <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase' }}>{title}</div>
        <p style={{ margin: '12px 0 0', fontSize: 13, lineHeight: 1.6, color: 'rgba(255,255,255,0.8)' }}>{message}</p>
        <div className="grid" style={{ marginTop: 20, gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 10 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="pr-confirm-cancel disabled:opacity-50"
            style={{ ...btn, background: 'transparent', color: '#fff', border: '2px solid #fff' }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="pr-confirm-go disabled:opacity-50"
            style={{ ...btn, background: '#FF2D2D', color: '#fff', border: '2px solid #FF2D2D' }}
          >
            {isLoading ? 'PROCESSING...' : confirmLabel}
          </button>
        </div>
        <style>{`
          .pr-confirm-cancel:hover:not(:disabled) { background: #fff !important; color: #000 !important; }
          .pr-confirm-go:hover:not(:disabled) { background: #000 !important; color: #FF2D2D !important; }
        `}</style>
      </div>
    </div>
  );
}

/**
 * Alert modal for success/error messages
 */
export function AlertModal({
  isOpen,
  onClose,
  title,
  message,
  type = 'info', // 'info', 'success', 'error', 'warning'
  theme = 'light',
}) {
  if (theme === 'admin') {
    return (
      <AdminModal
        open={isOpen}
        onClose={onClose}
        title={title || (type === 'error' ? 'THAT DID NOT WORK' : 'HEADS UP')}
        footer={<Btn size="sheet" variant="solid" onClick={onClose}>OK</Btn>}
        width={440}
      >
        <p className="m-0 font-mono text-[13px] leading-[1.65]" style={{ color: type === 'error' ? '#B91C1C' : '#4B5563' }}>{message}</p>
      </AdminModal>
    );
  }
  // The glyph sits in a soft disc: the ring colour says what kind of news it is.
  const tone = {
    info: { bg: '#F2F2F2', fg: '#000' },
    success: { bg: '#DCFCE7', fg: '#15803D' },
    error: { bg: '#FEE2E2', fg: '#DC2626' },
    warning: { bg: '#FEF3C7', fg: '#B45309' },
  }[type] || { bg: '#F2F2F2', fg: '#000' };
  const paths = {
    info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    success: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    error: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
    warning: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      showCloseButton={false}
      footer={
        <div className="flex w-full justify-center">
          <Button variant="primary" onClick={onClose} className="min-w-[140px]">
            OK
          </Button>
        </div>
      }
    >
      <div className="flex flex-col items-center text-center">
        <div
          className="mb-4 flex h-14 w-14 items-center justify-center rounded-full"
          style={{ background: tone.bg, color: tone.fg }}
          aria-hidden="true"
        >
          <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d={paths[type] || paths.info} />
          </svg>
        </div>
        {title && (
          <h3 className="mb-2 font-mono text-[14px] font-bold uppercase tracking-[0.14em] text-black">
            {title}
          </h3>
        )}
        <p className="m-0 font-mono text-[13px] leading-[1.65] text-[#4B5563]">
          {message}
        </p>
      </div>
    </Modal>
  );
}
